import { HttpInterceptorFn, HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';
import { ServerErrorModalService } from '@app/utils/services/server-error-modal.service';
import { SKIP_API_KEY } from '@app/data/data-sources/remote/postal-code-remote.data-source';

/**
 * Solo considera error de servidor:
 * - Códigos 5xx
 * - Errores de red (sin respuesta del servidor)
 * NO muestra modal para errores 4xx (petición mal formada, no autorizado, etc.)
 */
function isServerError(error: HttpErrorResponse): boolean {
  if (error.status === 0) {
    return true;
  }
  return error.status >= 500 && error.status < 600;
}

/**
 * URLs para las que NO se muestra modal ante errores 5xx/red — el caller las maneja solo
 * (fallback a cache, valores por defecto, etc.). Agregar aqui cualquier endpoint opcional
 * cuya falla no debe interrumpir al usuario.
 */
const SILENT_ERROR_URL_PATTERNS: RegExp[] = [
  /\/api\/v1\/tenants\/[^/]+\/theme\/?$/, // Tenant theme — aun no implementado en backend
];

function isSilentErrorRequest(req: HttpRequest<unknown>): boolean {
  // Peticiones marcadas con `SKIP_API_KEY=true` son a terceros (SEPOMEX, etc.)
  // — no mostrar modal de error: el caller maneja el fallo retornando null y
  // dejando al usuario continuar el flujo manualmente.
  if (req.context.get(SKIP_API_KEY)) return true;
  return SILENT_ERROR_URL_PATTERNS.some((pattern) => pattern.test(req.url));
}

export const serverErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const modalService = inject(ServerErrorModalService);
  const transloco = inject(TranslocoService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (isServerError(error) && !isSilentErrorRequest(req)) {
        modalService.show({
          text: transloco.translate('errors.serverError'),
          buttonLabel: transloco.translate('common.accept'),
        });
      }
      return throwError(() => error);
    })
  );
};
